import { motion } from 'motion/react';
import { WhatsappLogo } from '@phosphor-icons/react';
import { useLocation } from 'react-router-dom';
import { buildWAUrl, getPageMessage } from '../lib/whatsapp';
import { trackWhatsAppClick } from '../lib/analytics';

/**
 * Global floating WhatsApp CTA.
 * - Desktop: green icon panel + navy text panel with "WhatsApp Us"
 * - Mobile: same pill, shorter label - sits above the MobileBottomBar (bottom-[82px])
 * - Gold pulse dot draws attention without being intrusive
 */
export default function WhatsAppButton() {
  const { pathname } = useLocation();
  const url = buildWAUrl(getPageMessage(pathname));
  const isFlightsPage = pathname === '/flights';
  const hideForCorporateHero = pathname === '/corporate-travel';

  return (
    <motion.a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackWhatsAppClick('floating_button')}
      initial={{ opacity: 0, y: 24, scale: 0.85 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay: 1.5, type: 'spring', stiffness: 260, damping: 22 }}
      whileHover={{ y: -3 }}
      whileTap={{ scale: 0.95 }}
      className={`fixed z-[70]
                 bottom-[82px] right-4
                 ${isFlightsPage ? 'md:bottom-2 md:right-2 md:scale-75 md:origin-bottom-right' : 'md:bottom-5 md:right-5 md:scale-90 md:origin-bottom-right'}
                 ${hideForCorporateHero ? 'hidden' : 'flex'}
                 items-center overflow-hidden
                 rounded-2xl
                 shadow-[0_8px_32px_rgba(11,31,58,0.38)]
                 hover:shadow-[0_14px_44px_rgba(11,31,58,0.52)]
                 transition-shadow duration-300`}
    >
      {/* Gold pulsing notification dot */}
      <span
        className="absolute -top-1.5 -right-1.5 z-10 pointer-events-none flex h-4 w-4"
        aria-hidden="true"
      >
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#D4A843] opacity-70" />
        <span className="relative inline-flex h-4 w-4 rounded-full bg-[#D4A843] border-2 border-white" />
      </span>

      {/* -- Green icon panel (always visible) -- */}
      <div className="flex items-center justify-center w-[52px] h-[52px] bg-[#0F7A35] shrink-0">
        <WhatsappLogo size={24} weight="fill" className="text-white" />
      </div>

      {/* -- Desktop text panel -- */}
      <div className="hidden md:flex flex-col justify-center pl-4 pr-5 h-[52px] bg-[#071126]">
        <span className="text-[9px] font-black uppercase tracking-[0.22em] text-[#3FA9F5] leading-none mb-[5px]">
          Reply in minutes
        </span>
        <span className="text-[13px] font-black text-white leading-none whitespace-nowrap">
          WhatsApp Us
        </span>
      </div>

      {/* -- Mobile text panel -- */}
      <div className="flex md:hidden items-center pl-2.5 pr-4 h-[52px] bg-[#071126]">
        <span className="text-[11px] font-black text-white uppercase tracking-wide leading-none whitespace-nowrap">
          Chat
        </span>
      </div>
    </motion.a>
  );
}
